import type { ChatScreenAction } from '@/ai/screens/chat-screen-context';
import type { RendererDescriptor } from '@/ai/tools/types';

/** Clickable suggestion shown under the latest assistant response. */
export type ResponseAction = {
  label: string;
  /** Same-origin path: clicking navigates. */
  href?: string;
  /** Follow-up question: clicking sends it as a new message. */
  prompt?: string;
};

const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null);

const tickerHref = (ticker: string, tab: string) => `/ativo/${encodeURIComponent(ticker)}/${tab}`;

const rendererActions = (renderer: RendererDescriptor): ResponseAction[] => {
  const params = (renderer.params ?? {}) as Record<string, unknown>;
  switch (renderer.kind) {
    case 'ticker-summary': {
      const ticker = text(params.ticker);
      if (!ticker) return [];
      return [
        { label: `Abrir ${ticker}`, href: tickerHref(ticker, 'resumo') },
        { label: `Proventos de ${ticker}`, href: tickerHref(ticker, 'dividendos') },
        { label: `Fundos similares a ${ticker}`, prompt: `Quais fundos são similares a ${ticker}?` },
      ];
    }
    case 'fund-reports': {
      const ticker = text(params.ticker);
      return ticker ? [{ label: `Relatórios de ${ticker}`, href: tickerHref(ticker, 'relatorios') }] : [];
    }
    case 'portfolio-overview':
    case 'portfolio-positions': {
      const portfolioId = text(params.portfolioId);
      if (!portfolioId) return [];
      return [
        { label: 'Ver posições', href: `/carteira/${portfolioId}/ativos` },
        { label: 'Ver dividendos', href: `/carteira/${portfolioId}/dividendos` },
      ];
    }
    case 'market-overview':
      return [{ label: 'Abrir mercado', href: '/mercado' }];
    case 'screener-results':
      return [{ label: 'Abrir catálogo de fundos', href: '/catalogo' }];
    default:
      return [];
  }
};

/**
 * Actions for the latest assistant response: navigation/follow-up chips
 * derived from the renderers the tools produced, plus the screen-level
 * actions of the page the conversation is on. Deduplicated by label and
 * capped so the row stays light.
 */
export const getResponseActions = (
  renderers: RendererDescriptor[],
  screenActions: ChatScreenAction[] = [],
  limit = 4,
): ResponseAction[] => {
  const actions: ResponseAction[] = [
    ...renderers.flatMap(rendererActions),
    ...screenActions.map((action) => ({ label: action.label, href: action.href })),
  ];
  const seen = new Set<string>();
  return actions
    .filter((action) => {
      if (!action.href && !action.prompt) return false;
      if (seen.has(action.label)) return false;
      seen.add(action.label);
      return true;
    })
    .slice(0, limit);
};
